import React, {PureComponent} from "react"
import {flatten} from "lodash"
import gql from "graphql-tag"
import {MutationProps} from "react-apollo"
import {ApolloQueryResult} from "apollo-boost"
import {DataProxy} from "apollo-cache"

import {Mutation} from "@irati/utils/services/apollo"

import {Recognition, RecognitionFeedList, RecognitionInput, RecognitionSource, User} from "@irati/types"
import {feedQuery} from "../../query/Feed"
import {updateFeeds, addItemToFeed, getFeedEntityFromRecognition, FeedEntity} from "../../helpers/feed"
import {TEAMS, PRIVATE} from "../../../constants/share"
import RecognitionFragment from "../../fragments/Recognition"

const graphQLMutation = gql`
  mutation createRecognition($recognition: RecognitionInput!) {
    createRecognition(recognition: $recognition) {
      ...Recognition
    }
  }
  ${RecognitionFragment}
`

export interface NewRecognition extends Omit<RecognitionInput, "userIds" | "teamIds"> {
  userIds: User[],
  teamIds: string[],
  share: ShareOption,
  walletTransferId?: string
}

interface Props extends Partial<MutationProps> {
  recognition: NewRecognition,
  children: any
}

interface Data {
  data: {
    createRecognition: Recognition
  }
}

interface CacheResult {
  recognitionFeed: RecognitionFeedList
}

interface ShareOption {
  label: string,
  value: string
}

export function parseRecognition({userIds, share, walletTransferId, ...recognition}: NewRecognition): RecognitionInput {
  const isPrivate = share.value === PRIVATE
  let teamIds: string[] = []
  if (share.value === TEAMS) {
    if (userIds) {
      if (userIds[0].teamIds) {
        teamIds = flatten(userIds.map((user) => user.teamIds))
      } else {
        // app uses older user.teams = string[]
        teamIds = flatten(userIds.map((user) => user.teams))
      }
    }
  }

  return {
    ...recognition,
    walletTransferId,
    isPrivate,
    teamIds,
    source: RecognitionSource.Perkbox,
    userIds: userIds.map(({uuid}: {uuid: string}) => uuid)
  }
}

export default class CreateRecognitionMutation extends PureComponent<Props> {
  render() {
    const {recognition, children, ...props} = this.props
    return (
      <Mutation mutation={graphQLMutation} {...props}>
        {(mutate: any, apolloState: ApolloQueryResult<Data>) =>
          children(
            () =>
              mutate({
                variables: {
                  recognition: parseRecognition(recognition)
                },
                update: (cache: DataProxy, {data: {createRecognition: newRecognition}}: Data) => {
                  const newItem: FeedEntity = getFeedEntityFromRecognition(newRecognition)

                  updateFeeds(newRecognition)((feed: string) => {
                    const query = {...feedQuery, variables: {view: feed}}

                    let result: CacheResult | null
                    try {
                      result = cache.readQuery(query)
                    } catch (e) {
                      result = null
                    }

                    if (result) {
                      const {recognitionFeed} = result
                      const newFeed = addItemToFeed(recognitionFeed, newItem)

                      cache.writeQuery({
                        ...query,
                        data: {
                          recognitionFeed: newFeed
                        }
                      })
                    }
                  })
                }
              }),
            apolloState
          )
        }
      </Mutation>
    )
  }
}
